import React, {
  useContext, useMemo, useState, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import { Accordion } from 'anf-core-react';
import CheckoutPageContext from '../../context/CheckoutPageContext';
import { REMOVE_BAG_ITEM_MUTATION } from '../../gql/bagItem.gql';
import { FRANCHISE_COUNTRY_REPUDIATE, SHIP_MODE_TYPE } from '../../tools/constants';
import $window from '../../tools/window';
import Repudiation from '../Common/Repudiation/Repudiation';
import PopinsPickupOrder from './PopinsPickupOrder/PopinsPickupOrder';
import CheckoutSelectedStore from './CheckoutSelectedStore';
import DeliveryTabs from './DeliveryOptions/DeliveryTabs/DeliveryTabs';
import ShippingSpeed from './ShippingSpeed/ShippingSpeed';
import ShippingHandlingContainer from './ShippingHandling/ShippingHandlingContainer';
import ShippingAddressContainer from './ShippingAddress/ShippingAddressContainer';
import PudosSection from './PudosSection/PudosSection';
import PopinsSection from './PopinsSection/PopinsSection';
import GooglePay from './PaymentManager/GooglePay/GooglePay';
import ApplePay from './PaymentManager/ApplePay/ApplePay';
import Klarna from './PaymentManager/Klarna/Klarna';
import PayPal from './PaymentManager/PayPal/PayPal';
import BagList from '../Common/BagList/BagList';
import useSwitchesFromContext from '../../hooks/useSwitchesFromContext';

export { default as SubmitButtonWrapper } from './SubmitButton/SubmitButtonWrapper';

export function RepudiationWrapper() {
  const { checkoutPageState, setCheckoutPageState } = useContext(CheckoutPageContext);

  const [removeCheckoutItemMutation] = useMutation(REMOVE_BAG_ITEM_MUTATION, {
    onCompleted: (removeCheckoutItem) => {
      if (removeCheckoutItem?.removeBagItem?.success) {
        if (Object.keys(removeCheckoutItem?.removeBagItem?.bagItems?.items)?.length === 0) {
          $window.location.reload();
        } else {
          setCheckoutPageState((previousState) => ({
            ...previousState,
            orderTotals: removeCheckoutItem?.removeBagItem?.orderTotals,
            bagItems: removeCheckoutItem?.removeBagItem?.bagItems,
            promoInfo: removeCheckoutItem?.removeBagItem?.promoInfo,
            repudiationData: removeCheckoutItem?.removeBagItem?.repudiationData,
            shippingSpeed: removeCheckoutItem?.removeBagItem?.shippingSpeed,
          }));
        }
      }
    },
  });

  const hasRepudiation = useMemo(
    () => checkoutPageState?.repudiationData?.repudiationProducts?.items?.length > 0,
    [checkoutPageState?.repudiationData?.repudiationProducts?.items],
  );

  const { repudiationData: { repudiationType = '' } = {} } = checkoutPageState || {};
  const hasFranchiseCountryRepudiation = repudiationType === FRANCHISE_COUNTRY_REPUDIATE;

  const hasInventoryRepudiation = (checkoutPageState?.repudiationData?.repudiationType === 'SOLDOUT_ITEM'
      || checkoutPageState?.repudiationData?.repudiationType === 'SFS_SOLDOUT_ITEM'
      || checkoutPageState?.repudiationData?.repudiationType === 'ZIPCODE_REPUDIATE')
    && checkoutPageState?.repudiationData?.errorMessage !== '';

  if (!hasRepudiation || hasFranchiseCountryRepudiation) {
    return null;
  }

  return (
    <div className="bag-repudiation">
      <Repudiation
        productList={checkoutPageState?.repudiationData?.repudiationProducts?.items}
        errorMessage={checkoutPageState?.repudiationData?.errorMessage}
        itemsToRemove={checkoutPageState?.repudiationData?.itemsToRemove}
        tmntData={checkoutPageState?.textFor}
        continueInCurrency={checkoutPageState?.repudiationData?.continueInCurrency}
        hasCheckoutButton={checkoutPageState?.repudiationData?.hasCheckoutButton}
        checkoutUrl={checkoutPageState?.repudiationData?.checkoutUrl}
        onRemoveItems={removeCheckoutItemMutation}
        showRemoveItemsButton={!hasInventoryRepudiation}
      />
    </div>
  );
}

export function DeliveryOptionsWrapper() {
  const { checkoutPageState } = useContext(CheckoutPageContext);
  const displayDeliveryOptions = !checkoutPageState?.bagItems?.hasOnlyEGiftCard;

  if (!displayDeliveryOptions) return null;

  return (
    <div data-testid="checkout-delivery-tabs">
      <DeliveryTabs />
    </div>
  );
}

export function ShippingSpeedWrapper() {
  const { checkoutPageState } = useContext(CheckoutPageContext);
  const displayDeliveryOptions = !checkoutPageState?.bagItems?.hasOnlyEGiftCard;

  if (!displayDeliveryOptions) return null;

  return (
    <ShippingSpeed />
  );
}

export function ShippingHandlingContainerWrapper() {
  const { checkoutPageState } = useContext(CheckoutPageContext);
  const { getSwitchValue } = useSwitchesFromContext();
  const isShippingHandlingModal = getSwitchValue('fulChkMfeDeliverySection');
  const displayDeliveryOptions = !checkoutPageState?.bagItems?.hasOnlyEGiftCard;

  if (!(displayDeliveryOptions && isShippingHandlingModal)) return null;

  return <ShippingHandlingContainer />;
}

export function ShippingAddressWrapper() {
  const { checkoutPageState } = useContext(CheckoutPageContext);
  const { getSwitchValue } = useSwitchesFromContext();
  const isShippingAddressEnabled = getSwitchValue('chkMfeShippingAddressEnabled');
  const { shipMethod: { shipModeType } = {} } = checkoutPageState || {};

  // Logic from CheckoutPage.jsx - show only for home delivery
  const isHomeDelivery = useMemo(() => shipModeType === SHIP_MODE_TYPE.HOME_DELIVERY
      || shipModeType === SHIP_MODE_TYPE.DIRECT_TO_DESK, [shipModeType]);
  const hasOnlyEGiftCard = checkoutPageState?.bagItems?.hasOnlyEGiftCard;
  const isShippingAddressFormVisible = isShippingAddressEnabled
    && isHomeDelivery
    && !hasOnlyEGiftCard;

  if (isShippingAddressFormVisible) {
    return <ShippingAddressContainer />;
  }
  return null;
}

ShippingAddressWrapper.validationFields = (checkoutPageState) => {
  const { shipMethod: { shipModeType } = {} } = checkoutPageState || {};
  const isHomeDelivery = shipModeType === SHIP_MODE_TYPE.HOME_DELIVERY
      || shipModeType === SHIP_MODE_TYPE.DIRECT_TO_DESK;
  const hasOnlyEGiftCard = checkoutPageState?.bagItems?.hasOnlyEGiftCard;
  const isShippingAddressFormVisible = isHomeDelivery && !hasOnlyEGiftCard;
  if (isShippingAddressFormVisible) {
    return {
      'addresses.homeDelivery.firstName': '',
      'addresses.homeDelivery.lastName': '',
      'addresses.homeDelivery.address1': '',
      'addresses.homeDelivery.city': '',
      'addresses.homeDelivery.state': '',
      'addresses.homeDelivery.postalCode': '',
    };
  }
  return {};
};

export function PudosSectionWrapper() {
  const { checkoutPageState } = useContext(CheckoutPageContext);
  const isPudosEnabled = checkoutPageState?.shipMethod
    && checkoutPageState.shippingAddress?.addressType === 'CP';

  if (isPudosEnabled && checkoutPageState.googleApiKey && checkoutPageState?.shipMethod?.shipModeType === 'Pudos') {
    return (
      <div className="pudos-section">
        <PudosSection tmntData={checkoutPageState?.textFor} />
      </div>
    );
  }
  return null;
}

export function PopinsSectionWrapper() {
  const { checkoutPageState } = useContext(CheckoutPageContext);
  const isPopinsEnabled = checkoutPageState?.shipMethod?.shipModeType === 'Popins'
    && checkoutPageState?.shippingAddress?.addressType === 'P';

  if (isPopinsEnabled && checkoutPageState.googleApiKey && checkoutPageState?.shipMethod?.shipModeType === 'Popins') {
    return (
      <div className="popins-section">
        <PopinsSection tmntData={checkoutPageState?.textFor} />
      </div>
    );
  }
  return null;
}

export function PickupOrderWrapper() {
  const { checkoutPageState } = useContext(CheckoutPageContext);
  const { shipMethod: { shipModeType } = {} } = checkoutPageState || {};
  const isHomeDelivery = useMemo(() => [
    SHIP_MODE_TYPE.HOME_DELIVERY,
    SHIP_MODE_TYPE.SAME_DAY_DELIVERY,
    SHIP_MODE_TYPE.DIRECT_TO_DESK,
  ].includes(shipModeType), [shipModeType]);
  const { getSwitchValue } = useSwitchesFromContext();
  const isShippingAddressEnabled = getSwitchValue('chkMfeShippingAddressEnabled');
  const isPickupFormVisible = useMemo(
    () => isShippingAddressEnabled && !isHomeDelivery,
    [isHomeDelivery, isShippingAddressEnabled],
  );
  return isPickupFormVisible ? <PopinsPickupOrder /> : null;
}

PickupOrderWrapper.validationFields = (checkoutPageState) => {
  const { shipMethod: { shipModeType } = {} } = checkoutPageState || {};
  const isHomeDelivery = [
    SHIP_MODE_TYPE.HOME_DELIVERY,
    SHIP_MODE_TYPE.SAME_DAY_DELIVERY,
    SHIP_MODE_TYPE.DIRECT_TO_DESK,
  ].includes(shipModeType);
  const isPickupFormVisible = !isHomeDelivery;
  const pickupType = shipModeType?.toLowerCase();
  if (isPickupFormVisible) {
    return {
      [`addresses.${pickupType}.firstName`]: '',
      [`addresses..${pickupType}.lastName`]: '',
    };
  }
  return {};
};

export function CheckoutSelectedStoreWrapper() {
  const { checkoutPageState } = useContext(CheckoutPageContext);
  const isHomeDelivery = useMemo(() => {
    if (!checkoutPageState?.shipMethod) {
      return false;
    }
    const { shipModeType } = checkoutPageState.shipMethod;
    return [
      SHIP_MODE_TYPE.HOME_DELIVERY,
      SHIP_MODE_TYPE.SAME_DAY_DELIVERY,
      SHIP_MODE_TYPE.DIRECT_TO_DESK,
    ].includes(shipModeType);
  }, [checkoutPageState?.shipMethod]);
  return !isHomeDelivery ? <CheckoutSelectedStore /> : null;
}

CheckoutSelectedStoreWrapper.validationFields = (checkoutPageState) => {
  const { shipMethod: { shipModeType } = {} } = checkoutPageState || {};
  const isHomeDelivery = [
    SHIP_MODE_TYPE.HOME_DELIVERY,
    SHIP_MODE_TYPE.SAME_DAY_DELIVERY,
    SHIP_MODE_TYPE.DIRECT_TO_DESK,
  ].includes(shipModeType);
  const pickupType = shipModeType?.toLowerCase();
  if (!isHomeDelivery) {
    return {
      // all the selected store values ares set together using useEffect
      // when orgName is available, other values should be available.
      [`addresses.${pickupType}.orgName`]: '',
    };
  }
  return {};
};

export function GooglePayWrapper() {
  const { checkoutPageState, googlePayInitialized } = useContext(CheckoutPageContext);
  const { getSwitchValue } = useSwitchesFromContext();
  const useStateAsCountrty = getSwitchValue('chkUseStateAsCountryGooglepay');
  const googlePayScriptURLOverride = getSwitchValue('googlePayScriptUrlOverride');

  if (!googlePayInitialized) return null;

  return (
    <GooglePay
      googlePayConfig={checkoutPageState?.paymentConfig?.googlePay}
      shippingAddress={checkoutPageState?.shippingAddress}
      orderTotals={checkoutPageState?.orderTotals}
      countriesConfig={checkoutPageState?.orderSubmitLegalTerms?.countriesConfig}
      useStateAsCountrty={useStateAsCountrty}
      googlePayURLOverride={googlePayScriptURLOverride}
    />
  );
}

export function ApplePayWrapper() {
  const { checkoutPageState, updateCheckoutPageState } = useContext(CheckoutPageContext);
  const { getSwitchValue } = useSwitchesFromContext();
  const hasApplePayPXP = getSwitchValue('chkHasApplePayPxp');
  const hasApplePay = hasApplePayPXP && !!$window.ApplePaySession;

  if (!(hasApplePay && checkoutPageState?.paymentConfig?.applePay)) return null;

  return (
    <ApplePay
      applePayConfig={checkoutPageState?.paymentConfig?.applePay ?? {}}
      orderTotals={checkoutPageState?.orderTotals ?? {}}
      isExpressFlow={false}
      pageState={checkoutPageState}
      updatePageState={updateCheckoutPageState}
      hasApplePayPXP={hasApplePayPXP}
      countriesConfig={checkoutPageState?.orderSubmitLegalTerms?.countriesConfig}
    />
  );
}

export function KlarnaWrapper({ stepId }) {
  const { checkoutPageState } = useContext(CheckoutPageContext);
  if (!checkoutPageState?.klarnaState?.isEnabled) return null;
  return <Klarna stepId={stepId} />;
}

KlarnaWrapper.propTypes = {
  stepId: PropTypes.string.isRequired,
};

KlarnaWrapper.displayName = 'KlarnaWrapper';

export function PayPalWrapper() {
  const { checkoutPageState } = useContext(CheckoutPageContext);
  const { getSwitchValue } = useSwitchesFromContext();
  const hasPayPalSubmitButtonInMfe = getSwitchValue('chkHasMfeSubmitButtonForPaypalPayment');

  if (
    !(
      checkoutPageState?.paymentConfig?.payPal?.hasPayPal
            && hasPayPalSubmitButtonInMfe
    )
  ) {
    return null;
  }

  return (
    <PayPal
      payPalEnvironment={
                checkoutPageState?.paymentConfig?.payPal?.payPalEnvironment
            }
      payPalMerchantId={
                checkoutPageState?.paymentConfig?.payPal?.payPalMerchantId
            }
      PayPalInContextScriptSrc={
                checkoutPageState?.paymentConfig?.payPal?.PayPalInContextScriptSrc
            }
      tmntData={checkoutPageState?.textFor}
    />
  );
}

export function BagListWrapper() {
  const { checkoutPageState } = useContext(CheckoutPageContext);
  const [bagListExpanded, setBagListExpanded] = useState(false);

  const getSelectedShipInfo = useCallback((shippingSpeed) => {
    const selectedShipInfo = shippingSpeed?.filter((d) => d?.isSelected);
    return selectedShipInfo?.length ? selectedShipInfo[0] : {};
  }, []);

  if (!checkoutPageState?.bagItems?.items
    || Object.keys(checkoutPageState?.bagItems?.items).length === 0) {
    return null;
  }

  return (
    <div data-testid="stepped-checkout-baglist" className="stepped-checkout-baglist">
      <Accordion
        headingLevel={2}
        id="stepped-checkout-baglist-accordion"
        title={`${checkoutPageState?.textFor?.itemsLabel?.value} (${checkoutPageState?.orderTotals?.totalItemsInBag || 0})`}
        variant="boxed"
        isExpanded={bagListExpanded}
        onClick={() => { setBagListExpanded(!bagListExpanded); }}
      >
        <div data-testid="baglist" className="checkout-baglist">
          <BagList
            variant="rightRail"
            bagItems={checkoutPageState?.bagItems?.items}
            tmntData={checkoutPageState?.textFor}
            shippingInfo={getSelectedShipInfo(checkoutPageState?.shippingSpeed)}
          />
        </div>
      </Accordion>
    </div>
  );
}
